import {
  AirIcon,
  AllProductsIcon,
  AllRequestsIcon,
  CopyIcon,
  DeleteIcon,
  ExportRequestIcon,
  FileIcon,
  FreeSalesIcon,
  GoIcon,
  HomeIcon,
  LiveAnimalIcon,
  OrganicCertIcon,
  SeaIcon,
  TransferIcon,
  UploadIcon,
  WineIcon,
} from "@tc/ui-dfe/icons"
import {
  B2GEligibilityStatusType,
  CertificateType,
  ChemistryTestStatus,
  DestinationMarket,
  ExportRequestType,
  PackagingStatus,
  TransportModeCode,
  WineDisputedReason,
  DestinationMarket as WinemakingMarket,
} from "@tc/ui-dfe/query"

export * from "./defaultValues/exportRequest"
export * from "./supportingDocument"

export type { RequestType } from "./configurability"

export const TERMS_AND_CONDITIONS_LINK =
  "https://www.mpi.govt.nz/dmsdocument/65283-Terms-of-Use-for-MPI-Trade-Certification"
export const drawerWidth = 243
export const maxCertificateCount = 3
export const maxRecentRequestCount = 15
export const maxExportRequestProductCount = 10
export const maxConfigurableRequestProductCount = 120
export const maxConfigurableRequestDocumentCount = 50
export const maxExportRequestConfProductCount = 120
export const maxExportRequestConfProcessCount = 70
export const maxConfigurableRequestProcessCount = 70
export const maxProductsAllowedPerCertificate = 800
export const maxExportRequestSupportingDocumentCount = 3
export const defaultDateFormat = "DD MMM YYYY"
export const defaultDateTimeFormat = "DD MMM YYYY HH:mm"
export const MAX_EES_PACKAGE_PRODUCT_QUANTITY = 15
export const MAX_CONTAINERS_PER_PRODUCT = 200
export const MAX_PACKAGE_COUNT_PER_PRODUCT = 3
export const PACKAGE_LABELS = [
  "Outermost Packaging Details",
  "Intermediate Packaging Details",
  "Innermost Packaging Details",
]

export const requestPerPage = 20
export const MenuIcons = Object.freeze({
  "/": <HomeIcon fontSize="small" />,
  "/requests": <AllRequestsIcon fontSize="small" />,
  "/transfer-requests": <TransferIcon fontSize="small" />,
  "/products": <AllProductsIcon fontSize="small" />,
  "/request/transfer/start": <GoIcon fontSize="small" />,
  "/export-request": <ExportRequestIcon fontSize="small" />,
  "/request/export/start": <ExportRequestIcon fontSize="small" />,
  "/supporting-documents": <FileIcon fontSize="small" />,
  "/xml-submit": <UploadIcon fontSize="small" />,
})
export const YesNoOptions = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
]

export enum AlertPortalType {
  AppPortal = "app-alert-container",
  FormPortal = "form-alert-container",
}

export const ProductItemMoreOptions = [
  {
    value: "duplicate",
    label: "Duplicate product",
    icon: <CopyIcon />,
    disabled: false,
  },
  {
    value: "remove",
    label: "Remove product",
    icon: <DeleteIcon fill="error.main" />,
    styles: {
      color: "error.main",
    },
    disabled: false,
  },
]

export const CertificateIconMap = {
  [CertificateType.FreeSale]: <FreeSalesIcon sx={{ width: "24px" }} />,
  [CertificateType.Organics]: <OrganicCertIcon sx={{ width: "24px" }} />,
  [CertificateType.LiveAnimals]: <LiveAnimalIcon sx={{ width: "24px" }} />,
  [CertificateType.FreeSaleStatement]: <FreeSalesIcon sx={{ width: "24px" }} />,
  [CertificateType.ExportEligibilityStatement]: (
    <WineIcon sx={{ width: "24px" }} />
  ),
  [CertificateType.Supplementary]: <WineIcon sx={{ width: "24px" }} />,
  [CertificateType.Catch]: <ExportRequestIcon sx={{ width: "24px" }} />,
  [CertificateType.Halal]: <ExportRequestIcon sx={{ width: "24px" }} />,
  [CertificateType.Nonconsignment]: (
    <ExportRequestIcon sx={{ width: "24px" }} />
  ),
  [CertificateType.OecdSeedVarietal]: (
    <ExportRequestIcon sx={{ width: "24px" }} />
  ),
  [CertificateType.OtherDocument]: <ExportRequestIcon sx={{ width: "24px" }} />,
  [CertificateType.Phytosanitary]: <ExportRequestIcon sx={{ width: "24px" }} />,
  [CertificateType.ProductEstablishmentRegistrationDocument]: (
    <ExportRequestIcon sx={{ width: "24px" }} />
  ),
  [CertificateType.ReexportPhytosanitary]: (
    <ExportRequestIcon sx={{ width: "24px" }} />
  ),
  [CertificateType.Sanitary]: <ExportRequestIcon sx={{ width: "24px" }} />,
}

export const ExportRequestDetailsSectionLabels = Object.freeze({
  request_details: "Request details",
  transport_details: "Transport details",
  products: "Products",
  declarations: "Declarations",
  supporting_documents: "Supporting documents",
  receiving_your_certificates: "Receiving your certificates",
})

export const WineProductSectionLabels = Object.freeze({
  general_information: "General information",
  wine_processes: "Add Process",
})

export * from "./messages"

export const TransportIconMap = Object.freeze({
  [TransportModeCode.AirTransport]: (
    <AirIcon fill="primary.dark" sx={{ p: 1, height: "40px", width: "40px" }} />
  ),
  [TransportModeCode.FixedTransportInstallation]: null,
  [TransportModeCode.InlandWaterTransport]: null,
  [TransportModeCode.Mail]: null,
  [TransportModeCode.MaritimeTransport]: (
    <SeaIcon fill="primary.dark" sx={{ height: "40px", width: "40px" }} />
  ),
  [TransportModeCode.MultimodalTransport]: null,
  [TransportModeCode.NotApplicable]: null,
  [TransportModeCode.NotSpecified]: null,
  [TransportModeCode.RailTransport]: null,
  [TransportModeCode.RoadTransport]: null,
})

export const CertificateLabels: Record<CertificateType, string> = {
  [CertificateType.LiveAnimals]: "Zoosanitary Horses to Australia AUPER13",
  [CertificateType.FreeSale]: "Free Sale Certification FSC 1",
  [CertificateType.FreeSaleStatement]: "Free Sale Statement FSS 1",
  [CertificateType.Organics]: "Organic Official Assurance OP504.1",
  [CertificateType.ExportEligibilityStatement]: "Export Eligibility Statement",
  [CertificateType.Supplementary]: "Supplementary Certification",
  [CertificateType.Catch]: "Catch Certificate",
  [CertificateType.Halal]: "Halal Certificate",
  [CertificateType.Nonconsignment]: "Non-consignment Certificate",
  [CertificateType.OecdSeedVarietal]: "OECD Seed Varietal Certificate",
  [CertificateType.OtherDocument]: "Other Document",
  [CertificateType.Phytosanitary]: "Phytosanitary Certificate",
  [CertificateType.ProductEstablishmentRegistrationDocument]:
    "Product Establishment Registration Document",
  [CertificateType.ReexportPhytosanitary]:
    "Re-export Phytosanitary Certificate",
  [CertificateType.Sanitary]: "Sanitary Certificate",
}

export const ExportEligibilityLabels: Record<B2GEligibilityStatusType, string> =
  {
    [B2GEligibilityStatusType.Eligible]: "Eligible",
    [B2GEligibilityStatusType.NotEligible]: "Not eligible",
    [B2GEligibilityStatusType.NotRequested]: "Not Requested",
    [B2GEligibilityStatusType.Requested]: "Requested",
    [B2GEligibilityStatusType.Expired]: "Expired",
    [B2GEligibilityStatusType.Incomplete]: "Incomplete",
  }

export enum CountryCode {
  Australia = "AU",
  China = "CN",
  GreatBritain = "GB",
  Japan = "JP",
  Singapore = "SG",
  Taiwan = "TW",
}

export enum CertificateSigningOfficeEnum {
  Auckland = "auckland",
  Christchurch = "christchurch",
}

const CertificateSigningOfficeLabels: Record<
  CertificateSigningOfficeEnum,
  string
> = {
  [CertificateSigningOfficeEnum.Auckland]: "Auckland",
  [CertificateSigningOfficeEnum.Christchurch]: "Christchurch",
}

const FreeSalesCertificateSigningOfficeLabels: Record<string, string> = {
  [CertificateSigningOfficeEnum.Auckland]: "Auckland",
}

export const getSigningOffices = (certType: CertificateType) => {
  return certType === CertificateType.FreeSale
    ? FreeSalesCertificateSigningOfficeLabels
    : CertificateSigningOfficeLabels
}

export enum CommodityEnum {
  BeeProducts = "BEE_PRODUCTS",
  DairyAndDairyProducts = "DAIRY_AND_DAIRY_PRODUCTS",
  Forestry = "FORESTRY",
  LiveAnimals = "LIVE_ANIMALS",
  MeatAndMeatProducts = "MEAT_AND_MEAT_PRODUCTS",
  NzGrapeWine = "NZ_WINE",
  OtherAnimalProducts = "OTHER_ANIMAL_PRODUCTS",
  PetFoodAndAnimalFeed = "PET_FOOD_AND_ANIMAL_FEED",
  PlantsAndPlantProducts = "PLANTS_AND_PLANT_PRODUCTS",
  ProcessedFoodAndBeverages = "PROCESSED_FOOD_AND_BEVERAGES",
  Seafood = "SEAFOOD",
}

export const CommodityLabels: Record<string, string> = Object.freeze({
  BEE_PRODUCTS: "Bee products",
  DAIRY_PRODUCTS: "Dairy and dairy products",
  FORESTRY: "Forestry",
  LIVE_ANIMALS: "Live animals",
  MEAT_PRODUCTS: "Meat and meat products",
  NZ_WINE: "NZ grape wine",
  ANIMAL_PRODUCTS: "Other animal products",
  ANIMAL_FOOD: "Pet food and animal feed",
  PLANT_PRODUCTS: "Plant and plant products",
  PROCESSED_FOOD: "Processed food and beverages",
  SEAFOOD: "Seafood",
})

export const TransportMethod: Partial<Record<TransportModeCode, string>> = {
  [TransportModeCode.AirTransport]: "Air",
  [TransportModeCode.MaritimeTransport]: "Sea",
}

export enum FileTypeEnum {
  DOCX = "docx",
  XLSX = "xlsx",
  PDF = "pdf",
  TXT = "txt",
  JPEG = "jpeg",
  JPG = "jpg",
  PNG = "png",
  RTF = "rtf",
  CSV = "csv",
}

export const FileTypeMapping: Record<
  FileTypeEnum,
  { accept: string; match: RegExp }
> = {
  [FileTypeEnum.DOCX]: {
    accept:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    match:
      /^application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document$/,
  },
  [FileTypeEnum.XLSX]: {
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    match:
      /^application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet$/,
  },
  [FileTypeEnum.PDF]: {
    accept: "application/pdf",
    match: /^application\/pdf$/,
  },
  [FileTypeEnum.TXT]: {
    accept: "text/plain",
    match: /^text\/plain$/,
  },
  [FileTypeEnum.JPEG]: {
    accept: "image/jpeg",
    match: /^image\/jpeg$/,
  },
  [FileTypeEnum.JPG]: {
    accept: "image/jpeg",
    match: /^image\/(jpeg|jpg)$/,
  },
  [FileTypeEnum.PNG]: {
    accept: "image/png",
    match: /^image\/png$/,
  },
  [FileTypeEnum.RTF]: {
    accept: "application/rtf",
    match: /^application\/rtf$/,
  },
  [FileTypeEnum.CSV]: {
    accept: "text/csv",
    match: /^text\/csv$/,
  },
}

export enum ExportRequestAction {
  Create = "Create",
  Draft = "Draft",
  SaveProduct = "SaveProduct",
  Review = "Review",
  Submit = "Submit",
  Update = "Update",
  Resubmit = "Resubmit",
}

export enum RequestFormType {
  Export = "export",
  Transfer = "transfer",
  Phyto = "phyto",
}

export enum SensoryProcessDeclarationResponse {
  false = "Confirmed",
  true = "Disputed",
}

export const DisputedReason: Record<WineDisputedReason, string> = {
  [WineDisputedReason.Oxidised]: "Oxidised",
  [WineDisputedReason.Tainted]: "Tainted",
  [WineDisputedReason.Malodorous]: "Malodorous",
}

export enum ProcessFormButtonLabel {
  done = "Done",
  save = "Save",
  requestConfirm = "Request confirmation",
}

export enum ChemistryTestRequiredLabels {
  EUBulkAndPackagedDeAlcoholisedWine = "EU - Bulk and packaged de-alcoholised wine",
  EUBulkWine = "EU - Bulk wine",
  EUPackagedWine = "EU - Packaged wine",
  BrazilBulkAndPackagedWine = "Brazil - Bulk and packaged wine",
}

export const ExportRequestStatusUnsaved = "Unsaved"

export const PackagingStatusLabels = Object.freeze({
  [PackagingStatus.NotRequested]: "Not requested",
  [PackagingStatus.Requested]: "Requested",
  [PackagingStatus.Confirmed]: "Confirmed",
  [PackagingStatus.Disputed]: "Disputed",
})

export const WinemakingMarketLabels = Object.freeze({
  [WinemakingMarket.NotApplicable]: "General",
  // [WinemakingMarket.Australia]: "Australia",
  [WinemakingMarket.Brazil]: "Brazil",
  // [WinemakingMarket.China]: "China",
  [WinemakingMarket.Eu]: "European Union",
  [WinemakingMarket.GreatBritain]: "Great Britain",
  // [WinemakingMarket.Japan]: "Japan",
  // [WinemakingMarket.Singapore]: "Singapore",
  // [WinemakingMarket.Taiwan]: "Taiwan",
})

export const WinemakingStatusDescription = Object.freeze({
  [WinemakingMarket.NotApplicable]:
    "I confirm that this wine batch was made under a valid WSMP and the details are correct",
  [WinemakingMarket.GreatBritain]:
    "I confirm that the details and declarations provided are correct",
  [WinemakingMarket.Eu]:
    "I confirm that the details and declarations provided are correct",
  [WinemakingMarket.Brazil]:
    "I confirm that the details and declarations provided are correct",
})

export enum WinePackagingConfirmationDecision {
  true = "Confirm",
  false = "Dispute",
}

export enum WinemakingConfirmationDecision {
  true = "Confirmed",
  false = "Disputed",
  null = "Not applicable",
}

export const ChemistryTestStatusLabel = {
  [ChemistryTestStatus.Cancelled]: "Cancelled",
  [ChemistryTestStatus.Fail]: "Fail",
  [ChemistryTestStatus.InProgress]: "In progress",
  [ChemistryTestStatus.Pass]: "Pass",
  [ChemistryTestStatus.Requested]: "Requested",
  [ChemistryTestStatus.Unsuitable]: "Unsuitable",
  [ChemistryTestStatus.CancellationRequested]: "Cancellation Requested",
  [ChemistryTestStatus.Error]: "Error",
}

export const alphanumeric = /^[a-zA-Z0-9_ ]*$/

export const alphanumericAndSpecialCharacters =
  //eslint-disable-next-line
  /^[a-zA-Z0-9\/_\\.;:&+',\-() ]*$/

export const characterCode32to126 =
  // code 32 to 126 except pipe(|)
  /^[^|][\x20-\x7EÉÖàáãäèéñòóôöùüĀÀÂÁÃÄÆÇĒÈÊËĪÎÍÏÑŌÒÔÓÕŒŪÙÛÚÜŸāâæçēêëīîíïōõœūûúÿ][^|]*$/

export const destinationMarketList = {
  [DestinationMarket.Eu]: "Eu",
  [DestinationMarket.GreatBritain]: "Great Britain",
  [DestinationMarket.Brazil]: "Brazil",
}

export const KB = 1024
export const MB = KB * KB
export const defaultMaxFileSize = 10 * MB

export enum FileErrorType {
  FileSize = "FileSize",
  FileType = "FileType",
  FileUpload = "FileUpload",
  FileName = "FileName",
}

export const idTransportField = {
  [CertificateType.FreeSale]: {
    label: "ID of transport",
    required: true,
    description: "Voyage or airway bill number for the consignment leaving NZ",
  },
  [CertificateType.ExportEligibilityStatement]: {
    label: "Airwaybill/Vessel voyage number",
    required: false,
    description: "Required for free sale certificate",
  },
}

export const MPI_USER_GUIDE_URL =
  "https://www.mpi.govt.nz/dmsdocument/66183-MPI-Trade-Certification-user-guide-wine-businesses"
export const MPI_EU_OMAR_REQUIREMENT_URL =
  "https://www.mpi.govt.nz/dmsdocument/34896-European-Union-Wine-OMAR"
export const NO_INGREDIENT = "No ingredients required on the VI-1 document"

export const DocumentTypeOptions = [
  { label: "Inspection record", value: "INSP" },
  { label: "Declaration of Conformity (DoC)", value: "DOC" },
  { label: "Treatment certificate", value: "TRT" },
  { label: "Import Permit", value: "IP" },
  { label: "Other", value: "OTHER" },
  { label: "Overseas Multiplication Agreement", value: "OMA" },
  { label: "Overseas Phyto Certificate", value: "OPC" },
  { label: "Seed Laboratory Test Result", value: "SLAB" },
]

export const ExportDataMenuOptions: {
  id: ExportRequestType
  description: string
  disabled?: boolean
}[] = [
  {
    id: ExportRequestType.AnimalProducts,
    description: "Animal Products",
    disabled: true,
  },
  {
    id: ExportRequestType.PlantsProducts,
    description: "Plants Products",
  },
  {
    id: ExportRequestType.Wine,
    description: "NZ grape wine and other products",
  },
]
